import { MutableRefObject, RefObject, useEffect, useRef } from 'react';
import { getTwoFingerSpan } from '@pages/MediaViewer/AssetViewer/PdfCard/utils';
import { IAssetViewerStateContextValue } from '@context/AssetViewerStateContext';

const minZoom = 0.1;
const maxZoom = 3;

export type ImagePinchGestureState = {
  isActive: boolean;
  startSpan: number;
  baseZoom: number;
  focalX: number;
  focalY: number;
  startCenterX: number;
  startCenterY: number;
};

type UseMobileImagePinchZoomParams = {
  enabled: boolean;
  viewportRef: RefObject<HTMLElement | null>;
  imageRef: RefObject<HTMLImageElement | null>;
  canvasContainerRef: RefObject<HTMLElement | null>;
  imagePinchGestureRef: MutableRefObject<ImagePinchGestureState>;
  turnoverDegree: number;
  zoomFactor: number;
  setZoomFactor: IAssetViewerStateContextValue['setZoomFactor'];
};

export function useMobileImagePinchZoom({
  enabled,
  viewportRef,
  imageRef,
  canvasContainerRef,
  imagePinchGestureRef,
  turnoverDegree,
  zoomFactor,
  setZoomFactor
}: UseMobileImagePinchZoomParams) {
  const zoomRef = useRef(zoomFactor);
  zoomRef.current = zoomFactor;

  const turnoverDegreeRef = useRef(turnoverDegree);
  turnoverDegreeRef.current = turnoverDegree;

  const pendingPinchZoomRef = useRef<number | null>(null);
  const pendingPositionRef = useRef<{ left: number; top: number } | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!enabled || !viewport) return undefined;

    const pinch = imagePinchGestureRef.current;

    const getImageTransform = (nextZoom: number) =>
      `translateX(-50%) translateY(-50%) rotate(${turnoverDegreeRef.current}deg) scale(${nextZoom})`;

    const capturePinchAnchor = (touches: TouchList) => {
      const image = imageRef.current;
      if (!image) return;

      const viewportRect = viewport.getBoundingClientRect();
      const rawFocalX =
        touches.length >= 2
          ? (touches[0].clientX + touches[1].clientX) / 2
          : viewportRect.left + viewportRect.width / 2;
      const rawFocalY =
        touches.length >= 2
          ? (touches[0].clientY + touches[1].clientY) / 2
          : viewportRect.top + viewportRect.height / 2;

      pinch.focalX =
        Math.min(Math.max(rawFocalX, viewportRect.left), viewportRect.right) -
        viewportRect.left;
      pinch.focalY =
        Math.min(Math.max(rawFocalY, viewportRect.top), viewportRect.bottom) -
        viewportRect.top;
      pinch.startCenterX = image.offsetLeft;
      pinch.startCenterY = image.offsetTop;
    };

    const applyPinchPreview = (nextZoom: number) => {
      const image = imageRef.current;
      const canvas = canvasContainerRef.current;
      if (!image || !canvas || pinch.baseZoom <= 0) return;

      const scaleRatio = nextZoom / pinch.baseZoom;
      const nextLeft =
        pinch.startCenterX +
        (pinch.focalX - pinch.startCenterX) * (1 - scaleRatio);
      const nextTop =
        pinch.startCenterY +
        (pinch.focalY - pinch.startCenterY) * (1 - scaleRatio);

      image.style.transform = getImageTransform(nextZoom);
      image.style.left = `${nextLeft}px`;
      image.style.top = `${nextTop}px`;
      canvas.style.left = `${nextLeft}px`;
      canvas.style.top = `${nextTop}px`;
    };

    const commitPinchZoom = () => {
      const nextZoom = pendingPinchZoomRef.current;
      const nextPosition = pendingPositionRef.current;
      pendingPinchZoomRef.current = null;
      pendingPositionRef.current = null;

      if (nextZoom == null || nextZoom === zoomRef.current) return;

      const image = imageRef.current;
      const canvas = canvasContainerRef.current;
      if (image && canvas && nextPosition) {
        image.style.left = `${nextPosition.left}px`;
        image.style.top = `${nextPosition.top}px`;
        canvas.style.left = `${nextPosition.left}px`;
        canvas.style.top = `${nextPosition.top}px`;
      }

      setZoomFactor(nextZoom);
    };

    const onTouchStart = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (span < 10) return;

      if (!pinch.isActive) {
        pinch.isActive = true;
        pinch.startSpan = span;
        pinch.baseZoom = zoomRef.current;
        pendingPinchZoomRef.current = null;
        pendingPositionRef.current = null;
        capturePinchAnchor(touchEvent.touches);
      }
    };

    const onTouchMove = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (!pinch.isActive || touchEvent.touches.length < 2) return;
      const span = getTwoFingerSpan(touchEvent.touches);
      if (pinch.startSpan < 10) return;

      const nextZoom = Math.min(
        maxZoom,
        Math.max(minZoom, pinch.baseZoom * (span / pinch.startSpan))
      );
      const scaleRatio = nextZoom / pinch.baseZoom;

      touchEvent.preventDefault();
      pendingPinchZoomRef.current = nextZoom;
      pendingPositionRef.current = {
        left:
          pinch.startCenterX +
          (pinch.focalX - pinch.startCenterX) * (1 - scaleRatio),
        top:
          pinch.startCenterY +
          (pinch.focalY - pinch.startCenterY) * (1 - scaleRatio)
      };
      applyPinchPreview(nextZoom);
    };

    const onTouchEnd = (e: Event) => {
      const touchEvent = e as TouchEvent;
      if (touchEvent.touches.length < 2 && pinch.isActive) {
        pinch.isActive = false;
        commitPinchZoom();
      }
    };

    const listenerCapturePassive = { capture: true, passive: true } as const;
    const listenerCaptureNotPassive = {
      capture: true,
      passive: false
    } as const;

    viewport.addEventListener(
      'touchstart',
      onTouchStart,
      listenerCapturePassive
    );
    viewport.addEventListener(
      'touchmove',
      onTouchMove,
      listenerCaptureNotPassive
    );
    viewport.addEventListener('touchend', onTouchEnd, listenerCapturePassive);
    viewport.addEventListener(
      'touchcancel',
      onTouchEnd,
      listenerCapturePassive
    );

    return () => {
      pinch.isActive = false;
      pendingPinchZoomRef.current = null;
      pendingPositionRef.current = null;
      viewport.removeEventListener(
        'touchstart',
        onTouchStart,
        listenerCapturePassive
      );
      viewport.removeEventListener(
        'touchmove',
        onTouchMove,
        listenerCaptureNotPassive
      );
      viewport.removeEventListener(
        'touchend',
        onTouchEnd,
        listenerCapturePassive
      );
      viewport.removeEventListener(
        'touchcancel',
        onTouchEnd,
        listenerCapturePassive
      );
    };
  }, [
    enabled,
    viewportRef,
    imageRef,
    canvasContainerRef,
    imagePinchGestureRef,
    setZoomFactor
  ]);
}
